import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { IQuotationCost, IQuotationXrates } from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { INumberFieldSpecs } from 'libraries/module/formbuilder/fields/number/INumberFieldSpecs';
import { ICurrencyRate } from "libraries/service/resources/currencies/currencies-resource.service";
import { CurrencyType } from "libraries/component/price/priceRealAmount.component";
import { CurrencyService, ICurrencyRateLegacy } from "libraries/service/currency/currency.service";

@Component({
    selector: 'td-price',
    template: `
        <ng-template #template>
            <td mat-cell
                #cell
                class="block-value"
                [attr.colspan]="colspan"
                [class.oddElement]="parity === 'odd'"
                [class.evenElement]="parity === 'even'"
                [class.is-editable]="isEditable"
                [class.small-height]="isSmallHeight"
                [ngStyle]="style"
                (click)="focusField()">
                <span *ngIf="!price || (!price.amount && price.amount !==0)">-</span>
                <div>
                    <div>
                        <price-real-amount *ngIf="price"
                                           [value]="price.amount"
                                           [currency]="canShowCurrency ? price.currency: null"
                                           [showZero]="true"
                                           [currencyType]="currencyType"
                                           [type]="type"></price-real-amount>
                    </div>
                    <div class="footer" *ngIf="quotationCurrencyCost">
                        <price-real-amount [value]="quotationCurrencyCost.amount"
                                           [currency]="quotationCurrencyCost.currency"
                                           [currencyType]="currencyType"
                                           type="small"></price-real-amount>
                    </div>
                </div>

                <input type="text"
                       (focus)="setEdit()"
                       #focuser
                       class="focuser"
                       *ngIf="price && isEditable">
            </td>
        </ng-template>
    `,
    styles: [`
        :host {
            display: none
        }

        td.block-value {
            font-size: 1rem;
            color: #414141;
            padding: .75rem;
            font-weight: normal;
            text-align: right;
            border-right: 1px solid white;
            border-bottom: 1px solid white;
            position: relative;
        }
        td.small-height{
            padding: .375rem .75rem;
        }

        td.is-editable {
            border-right: 1px solid rgba(189, 195, 199, 0.3);
            border-bottom: 1px solid rgba(189, 195, 199, 0.3);
            cursor: text;
        }

        td.is-editable.oddElement {
            background: #f9fafc;
        }

        td.is-editable.evenElement {
            background: #fefefe;
        }

        td:not(.is-editable).oddElement {
            background: #ecedef;
        }

        td:not(.is-editable).evenElement {
            background: #f1f1f1;
        }

        .focuser {
            opacity: 0;
            width: 0;
            height: 0;
            position: absolute;
            z-index: -10;
        }

        .footer {
            position: absolute;
            bottom: .05rem;
            right: 0.75rem;
            opacity: 0.75;
        }
    ` ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class TdPriceComponent implements OnInit, OnChanges, AfterViewInit {
    @Input()
    public fieldConfig: INumberFieldSpecs;
    @Input()
    public colspan: number = 1;
    @Input()
    public price: IQuotationCost;
    @Input()
    public isSticky: boolean;
    @Input()
    public leftPosition: number;
    @Input()
    public quotationCurrency: string = "EUR";
    @Input()
    public parity: "odd" | "even" = "odd";
    @Input()
    public isEditable: boolean = false;
    @Input()
    public canHaveFooter: boolean = true;
    @Input()
    public forceFocus: boolean;
    @Input()
    public strong: boolean;
    @Input()
    public canEmitRightPosition: boolean;
    @Input()
    public isDynamicSticky: boolean = true;
    @Input()
    public canShowCurrency: boolean = true;
    @Input()
    public minWidth: string;
    @Input()
    public fixedWidth: string;
    @Input()
    public currencyXRates: IQuotationXrates[];
    @Input()
    public isSmallHeight: boolean;
    @Input()
    public rightEmissionAddition: number = 0;

    @Output()
    public readonly onRightPositionChange: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    public readonly onFocus: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    public readonly onPriceChange: EventEmitter<string> = new EventEmitter<string>();


    public quotationCurrencyCost: IQuotationCost;
    public originalPrice: IQuotationCost;
    public currencyType: CurrencyType = CurrencyType.code;
    public type: string = 'light smallCurrency';
    public style: { [key: string]: string } = {};

    @ViewChild('cell')
    public cell: ElementRef;
    @ViewChild('focuser')
    public focuser: ElementRef;

    @ViewChild('template', {read: TemplateRef, static: true})
    private readonly template: TemplateRef<{}>;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly currencyService: CurrencyService,
        private readonly renderer: Renderer2,
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);
        if (this.canHaveFooter) {
            this.computeFooter();
        }
        this.type = this.strong ? 'smallCurrency' : 'light smallCurrency';

    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.forceFocus && changes.forceFocus.currentValue && this.focuser) {
            setTimeout(()=>{
                this.focusField();
            });
        }

        if (changes.leftPosition && changes.leftPosition.currentValue) {
            this.style.left = `${changes.leftPosition.currentValue}px`;
        }
        if (changes.price && changes.price.currentValue) {
            if (this.cell) {
                setTimeout((): void => this.stickCell(), 10);
            }
            if (this.canHaveFooter) {
                this.computeFooter();
            }
        }
    }
    public ngAfterViewInit(): void {
        if(this.minWidth){
            this.renderer.setStyle(this.cell.nativeElement, 'min-width', this.minWidth);
        }
        if(this.fixedWidth){
            this.renderer.setStyle(this.cell.nativeElement, 'width', this.fixedWidth);
        }

        if(this.isSticky){
            setTimeout(
                (): void => {
                const left: number = this.leftPosition || this.cell.nativeElement.offsetLeft;
                this.renderer.setStyle(this.cell.nativeElement, 'position', 'sticky');
                this.renderer.setStyle(this.cell.nativeElement, 'z-index', '1');
                this.renderer.setStyle(this.cell.nativeElement, 'left', `${left}px`);
                if (this.canEmitRightPosition) {
                    this.onRightPositionChange.emit(left + this.cell.nativeElement.offsetWidth);
                }
            }, 10
            );

        }
    }

    public focusField(): void {
        if (this.price && this.isEditable && this.focuser) {
            this.focuser.nativeElement.focus();
        }
    }

    public setEdit(): void {
        this.originalPrice = { ...this.price };
        this.onFocus.emit(this.fixedWidth || `${this.cell.nativeElement.offsetWidth}px`);
    }

    private computeFooter(): void {
        if (this.price && this.quotationCurrency !== this.price.currency) {
            this.currencyService
                .searchCurrenciesRates(this.currencyXRates, this.price.currency, this.quotationCurrency)
                .subscribe((currencyRate: ICurrencyRateLegacy[]) => {
                    this.quotationCurrencyCost = {
                        currency: this.quotationCurrency,
                        amount: this.price.amount * currencyRate[0].rate
                    };
                });
        } else {
            this.quotationCurrencyCost = null;
        }
    }

    private stickCell(): void {
        if (this.isSticky) {
            if (this.isDynamicSticky) {
                const left: number = this.cell.nativeElement.offsetLeft;
                this.renderer.setStyle(this.cell.nativeElement, 'position', 'sticky');
                this.renderer.setStyle(this.cell.nativeElement, 'z-index', '1');
                this.renderer.setStyle(this.cell.nativeElement, 'left', `${this.leftPosition || left}px`);
                if (this.canEmitRightPosition && this.cell) {
                    setTimeout(
                        (): void=> {
                            this.onRightPositionChange.emit(left + this.cell.nativeElement.offsetWidth);
                        },
                    );
                }
            } else {
                const left: number = this.cell.nativeElement.offsetLeft;
                this.renderer.setStyle(this.cell.nativeElement, 'position', 'sticky');
                this.renderer.setStyle(this.cell.nativeElement, 'z-index', '1');
                this.renderer.setStyle(this.cell.nativeElement, 'left', `${left}px`);
            }
        }
    }

}
